import { motion } from "framer-motion";
import { Check, ArrowRight, Loader2, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import Particles from "@/components/landing/Particles";
import MobileMenu from "@/components/landing/MobileMenu";
import logoFull from "@/assets/orbitrank-logo-v3.png";
import { useState, useEffect } from "react";

const tiers = [
  {
    name: "Starter",
    cta: "Get Started",
    priceId: "starter_monthly",
    price: 59,
    description: "For small businesses getting started with SEO",
    features: [
      "1 location",
      "30 SEO pages / month",
      "Rank tracking",
      "20 directory backlinks",
    ],
    highlight: false,
  },
  {
    name: "Pro",
    cta: "Start Growing",
    priceId: "pro_monthly",
    price: 99,
    description: "For growing businesses that need more reach",
    features: [
      "3 locations",
      "90 SEO pages / month",
      "Rank tracking",
      "Competitor analysis",
      "Page speed monitoring",
      "20 directory backlinks",
      "Monthly AI report",
    ],
    highlight: true,
  },
  {
    name: "Enterprise",
    cta: "Scale Up",
    priceId: "enterprise_monthly",
    price: 199,
    description: "For businesses that want unlimited growth",
    features: [
      "Unlimited locations",
      "Unlimited SEO pages",
      "All Pro features",
      "Priority support",
    ],
    highlight: false,
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { openCheckout, loading: checkoutLoading } = usePaddleCheckout();
  const [user, setUser] = useState<{ id: string; email: string } | null>(null);
  const [loadingTier, setLoadingTier] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser({ id: session.user.id, email: session.user.email || "" });
      }
    });
  }, []);

  const handleSelect = async (tier: typeof tiers[0]) => {
    if (!user) {
      navigate("/auth");
      return;
    }

    setLoadingTier(tier.priceId);
    try {
      await openCheckout({
        priceId: tier.priceId,
        customerEmail: user.email,
        customData: { userId: user.id },
      });
    } finally {
      setLoadingTier(null);
    }
  };

  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="relative min-h-screen bg-background overflow-x-hidden">
      <Particles />
      <PaymentTestModeBanner />

      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        height: 56,
        padding: "12px 20px",
        overflow: "visible",
        background: "var(--lo-nav-bg)", backdropFilter: "blur(20px)",
        borderBottom: "1px solid var(--lo-border)",
      }}>
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Go to home"
          style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
        >
          <img
            src={logoFull}
            alt="OrbitRank logo"
            style={{ height: "28px", width: "auto", maxWidth: "none" }}
          />
        </button>
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          aria-expanded={menuOpen}
          style={{
            width: 40, height: 40, borderRadius: 8,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: "transparent", border: "1px solid var(--lo-border)",
            cursor: "pointer", color: "var(--lo-text)",
          }}
        >
          <Menu size={20} />
        </button>
      </nav>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      <div className="relative z-10 pt-28 pb-20 px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h1 className="text-3xl sm:text-5xl font-bold text-foreground mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-white text-sm sm:text-base max-w-lg mx-auto">
            Choose the plan that fits your growth. Cancel anytime.
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative rounded-xl border p-6 flex flex-col ${
                tier.highlight
                  ? "border-primary/40 bg-primary/[0.03] shadow-[0_0_40px_rgba(0,255,136,0.06)]"
                  : "border-border/30 bg-card/30"
              }`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="text-[10px] font-mono uppercase tracking-widest bg-primary/20 text-primary px-3 py-1 rounded-full border border-primary/30">
                    Most Popular
                  </span>
                </div>
              )}

              <h3 className="text-lg font-bold text-foreground mb-1">{tier.name}</h3>
              <p className="text-xs text-white/80 mb-4">{tier.description}</p>

              <div className="mb-6">
                <span className="text-4xl font-bold text-foreground">${tier.price}</span>
                <span className="text-sm text-white/70">/mo</span>
              </div>

              <ul className="space-y-2.5 mb-8 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-white/90">
                    <Check className="w-4 h-4 text-primary/70 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleSelect(tier)}
                disabled={loadingTier !== null}
                className="font-bold text-sm h-11 bg-primary text-black hover:bg-primary/90 mx-[5%] w-[90%]"
              >
                {loadingTier === tier.priceId ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {tier.cta} <ArrowRight className="w-4 h-4 ml-1.5" />
                  </>
                )}
              </Button>
              <p className="text-[11px] text-white text-center mt-2.5">
                7-day free trial · Cancel anytime · No commitment
              </p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center text-xs text-white font-mono mt-10"
        >
          All plans include a 30-day money-back guarantee
        </motion.p>
      </div>
    </div>
  );
};

export default Pricing;
